import React, { Component } from 'react'
import styled from 'styled-components'
import { connect } from 'react-redux'
import MapOverlay from './MapOverlay'
import MapCard from './MapCard'
import mapStyles from '../mapStyles.json'
import PropTypes from 'prop-types'
import { getPropertyImages } from '../../search-common/helpers'
import { createGoogleMapsImageMarker } from '../../modules/google-maps-loader'
import plotter from '../../../img/plotter.svg'

const MapContainer = styled.div`
  width: 100%;
  height: 100%;
  background-color: transparent;
  position: relative;
`

/**
 * GoogleMap component.
 */
class GoogleMap extends Component {
  /**
   * Constructor.
   *
   * @param {object} props
   */
  constructor( props ) {
    super( props )

    this.state = {
      map: null,
      markers: [],
      openMarker: null,
      overlayCenter: null
    }

    this.mapRef = React.createRef()
    this.renderMap = this.renderMap.bind( this )
    this.addMarkers = this.addMarkers.bind( this )
    this.removeMarkers = this.removeMarkers.bind( this )
    this.centerMap = this.centerMap.bind( this )
    this.addMarkerClickHandler = this.addMarkerClickHandler.bind( this )
    this.closeOverlay = this.closeOverlay.bind( this )
    this.setOverlayCenter = this.setOverlayCenter.bind( this )
    this.panToMap = this.panToMap.bind( this )
  }

  /**
   * Function renders the map to the <MapContainer>
   */
  renderMap() {
    const { googleMaps } = this.props
    return new Promise(( resolve, reject ) => {
      const args = {
        zoom: 13,
        styles: mapStyles,
        mapTypeControl: false,
        gestureHandling: 'greedy',
        maxZoom: 16,
        streetViewControl: false,
        center: new googleMaps.LatLng( 60.1694157, 24.9281607 )
      }

      const map = new googleMaps.Map( this.mapRef.current, args )
      if ( map ) {
        this.setState({ map: map })
        resolve()
      } else {
        reject( 'Error rendering the map.' )
      }
    })
  }

  /**
   * Function adds all markers to the map based on the
   * search results.
   */
  addMarkers() {
    const { results, googleMaps } = this.props
    const { map } = this.state
    return new Promise(( resolve, reject ) => {
      let markers = []
      this.setState({ markers: markers })
      results.forEach(( result ) => {
        const latLng = new googleMaps.LatLng( result.latitude, result.longitude )
        const marker = createGoogleMapsImageMarker( googleMaps, {
          position: latLng,
          map: map,
          resultId: result.id,
          resultRef: result,
          src: 'data:image/svg+xml;charset=UTF-8,' + encodeURIComponent( plotter ),
          size: 36
        })
        if ( !marker ) {
          reject( 'Could not create marker:', marker )
        }
        this.addMarkerClickHandler( marker )
        markers.push( marker )
      })
      this.setState({ markers: markers })
      resolve()
    })
  }

  /**
   * Function attaches click listeners to markers
   * to open the <MapOverlay>
   *
   * @param {google.maps.OverlayView} marker
   */
  addMarkerClickHandler( marker ) {
    marker.addListener( 'click', () => {
      this.setState({ openMarker: marker })
      setTimeout(() => {
        this.panToMap( marker )
      }, 200 )
    })
  }

  /**
   *
   * @param {google.maps.OverlayView} marker
   */
  panToMap( marker ) {
    const { map, overlayCenter } = this.state
    if ( overlayCenter ) {
      map.panTo( overlayCenter )
    } else {
      map.panTo( marker.position )
    }
  }

  /**
   * Function closes the open overlay.
   */
  closeOverlay() {
    this.setState({ openMarker: null, overlayCenter: null })
  }

  /**
   * Function removes all markers from the map.
   */
  removeMarkers() {
    const { markers } = this.state
    return new Promise(( resolve, reject ) => {
      markers.forEach(( marker ) => {
        try {
          marker.setMap( null )
        } catch ( error ) {
          reject( 'Could not remove marker:', error )
        }
      })
      resolve()
    })
  }


  /**
   * Function centers the map in bounds that all
   * markers are visible.
   */
  centerMap() {
    const { googleMaps } = this.props
    const { map, markers, overlayCenter } = this.state
    return new Promise(( resolve, reject ) => {
      let bounds = new googleMaps.LatLngBounds()
      if ( overlayCenter ) {
        bounds.extend( overlayCenter )
      } else {
        markers.forEach(( marker ) => {
          bounds.extend( marker.position )
        })
      }
      try {
        map.setOptions({ maxZoom: 16 })
        map.fitBounds( bounds )
        map.setOptions({ maxZoom: 18 })
        resolve()
      } catch ( error ) {
        reject( 'Could not center map:', error )
      }
    })
  }

  /**
   * Set the overlay center position.
   */
  setOverlayCenter( center ) {
    const { overlayCenter } = this.state
    if ( !overlayCenter ) {
      this.setState({
        overlayCenter: center
      })
    }
  }

  /**
   * componentDidUpdate
   *
   * @param {object} prevProps
   * @param {object} prevState
   */
  componentDidUpdate( prevProps ) {
    const { results } = this.props
    if ( results != prevProps.results ) {
      this.removeMarkers()
        .then(() => this.addMarkers())
        .then(() => this.centerMap())
        // eslint-disable-next-line no-console
        .catch(( error ) => console.error( error ))
    }
  }

  /**
   * componentDidMount
   */
  componentDidMount() {
    const { map } = this.state
    if ( map === null ) {
      this.renderMap()
        .then(() => this.addMarkers())
        .then(() => this.centerMap())
        // eslint-disable-next-line no-console
        .catch(( error ) => console.error( error ))
    }
  }

  /**
   * Render method.
   */
  render() {
    const { googleMaps, images } = this.props
    const { openMarker, map } = this.state
    return (
      <React.Fragment>
        <MapContainer ref={this.mapRef} />
        {openMarker
          ? <MapOverlay
            googleMaps={googleMaps}
            latLng={openMarker.position}
            map={map}
            setCenter={this.setOverlayCenter}
          >
            <MapCard
              resultItem={openMarker.resultRef}
              closeHandler={this.closeOverlay}
              images={getPropertyImages( openMarker.resultRef.uuid, images )}
            />
          </MapOverlay>
          : null
        }
      </React.Fragment>
    )
  }
}

GoogleMap.propTypes = {
  results: PropTypes.array.isRequired,
  googleMaps: PropTypes.any,
  images: PropTypes.array.isRequired
}

/**
 * Map the redux state to component props.
 *
 * @param {object} state
 * @return {object}
 */
const mapStateToProps = ( state ) => ({
  results: state.search.results,
  images: state.images.data
})

export default connect( mapStateToProps )( GoogleMap )
